/**
 * PasswordChange Component
 *
 * Form for changing user password with current password verification.
 * Includes password strength requirements and validation.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-151 - Shared Account Settings
 *
 * Security Requirements:
 * - Current password verification required
 * - New password must be at least 8 characters
 * - New password must match confirmation
 * - Passwords are never stored in local state after submission
 */

import React, { useState } from 'react';
import apiRequest from '../../services/api';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle as _AlertCircle, Shield, AlertCircle } from 'lucide-react';

export default function PasswordChange() {
  // Form state
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Password strength indicator
  const [passwordStrength, setPasswordStrength] = useState(null);

  /**
   * Calculate password strength
   */
  const calculatePasswordStrength = (password) => {
    if (!password) {return null;}

    let strength = 0;
    const checks = {
      length: password.length >= 8,
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[^a-zA-Z0-9]/.test(password),
    };

    strength += checks.length ? 1 : 0;
    strength += checks.hasLower ? 1 : 0;
    strength += checks.hasUpper ? 1 : 0;
    strength += checks.hasNumber ? 1 : 0;
    strength += checks.hasSpecial ? 1 : 0;

    const label = strength <= 2 ? 'Weak' : strength <= 3 ? 'Fair' : strength === 4 ? 'Good' : 'Strong';
    const color = strength <= 2 ? 'red' : strength <= 3 ? 'yellow' : strength === 4 ? 'blue' : 'green';

    return { strength, label, color, checks };
  };

  /**
   * Handle input changes
   */
  const handleChange = (  _e) => {
    const { name, value } = _e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Update password strength for new password
    if (name === 'new_password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    setSuccess(false);
    setError(null);
  };

  /**
   * Toggle password visibility
   */
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    if (!formData.current_password) {
      setError('Current password is required');
      return false;
    }

    if (!formData.new_password) {
      setError('New password is required');
      return false;
    }

    if (formData.new_password.length < 8) {
      setError('New password must be at least 8 characters long');
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('New password and confirmation do not match');
      return false;
    }

    if (formData.current_password === formData.new_password) {
      setError('New password must be different from current password');
      return false;
    }

    return true;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (  _e) => {
    _e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await apiRequest('/api/v1/account/password', {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        setSuccess(true);
        // Clear form after successful password change
        setFormData({
          current_password: '',
          new_password: '',
          confirm_password: '',
        });
        setPasswordStrength(null);

        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (err) {
      console.error('Error changing password:', err);

      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to change password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Change Password</h3>
      <p className="text-sm text-gray-600 mb-6">
        Update your password to keep your account secure. You&apos;ll need to enter your current password to confirm the change.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-lg">
        {/* Current Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Lock className="w-4 h-4 inline mr-2" />
            Current Password
            <span className="text-red-600 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              name="current_password"
              value={formData.current_password}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
              placeholder="Enter your current password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Shield className="w-4 h-4 inline mr-2" />
            New Password
            <span className="text-red-600 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              required
              minLength={8}
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
              placeholder="Enter your new password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Password Strength Indicator */}
          {passwordStrength && formData.new_password && (
            <div className="mt-2">
              <div className="flex items-center gap-2 mb-1">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all bg-${passwordStrength.color}-600`}
                    style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-medium text-${passwordStrength.color}-700`}>
                  {passwordStrength.label}
                </span>
              </div>
              <ul className="text-xs text-gray-600 space-y-0.5">
                <li className={passwordStrength.checks.length ? 'text-green-600' : ''}>
                  {passwordStrength.checks.length ? '✓' : '○'} At least 8 characters
                </li>
                <li className={passwordStrength.checks.hasLower && passwordStrength.checks.hasUpper ? 'text-green-600' : ''}>
                  {passwordStrength.checks.hasLower && passwordStrength.checks.hasUpper ? '✓' : '○'} Uppercase and lowercase letters
                </li>
                <li className={passwordStrength.checks.hasNumber ? 'text-green-600' : ''}>
                  {passwordStrength.checks.hasNumber ? '✓' : '○'} At least one number
                </li>
                <li className={passwordStrength.checks.hasSpecial ? 'text-green-600' : ''}>
                  {passwordStrength.checks.hasSpecial ? '✓' : '○'} Special character (!@#$%^&*)
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* Confirm New Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Shield className="w-4 h-4 inline mr-2" />
            Confirm New Password
            <span className="text-red-600 ml-1">*</span>
          </label>
          <div className="relative">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              required
              minLength={8}
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
              placeholder="Confirm your new password"
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              Password changed successfully! Please use your new password for future logins.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Change Password Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors
              ${loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
              }
            `}
          >
            <Lock className="w-4 h-4" />
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </div>
      </form>
    </div>
  );
}
